// src/App.js

import React, { Suspense, lazy, useEffect, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";

import Header from "./components/Header";
import Footer from "./components/Footer";
import ScrollToTop from "./ScrollToTop";
import TrackPageView from "./TrackPageView";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { initGA } from "./analytics";

// ---------------- LAZY LOADING ----------------

const Home = lazy(() => import("./pages/Home"));
const Women = lazy(() => import("./pages/Women"));
const Men = lazy(() => import("./pages/Men"));
const About = lazy(() => import("./pages/About"));
const NewArrivals = lazy(() => import("./pages/NewArrivals"));
const BestSeller = lazy(() => import("./pages/BestSeller"));
const ProductDetails = lazy(() => import("./pages/ProductDetails"));
const ShoppingCart = lazy(() => import("./pages/ShoppingCart"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Cart = lazy(() => import("./pages/Cart"));

const TermsPage = lazy(() =>
  import("./components/TermsAndConditions")
);

const PrivacyPolicyPage = lazy(() =>
  import("./components/PrivacyPolicy")
);

const ShippingDeliveryPage = lazy(() =>
  import("./components/ShippingDelivery")
);

const ReturnExchangePage = lazy(() =>
  import("./components/ReturnExchange")
);

const OrderSuccessPage = lazy(() =>
  import("./components/OrderSuccess")
);

const RefundPolicyPage = lazy(() =>
  import("./components/RefundPolicy")
);

const PaymentFailedPage = lazy(() =>
  import("./components/PaymentFailed")
);

const PaymentPendingPage = lazy(() =>
  import("./components/PaymentPending")
);

const PaymentStatus = lazy(() =>
  import("./components/PaymentStatus")
);

const CheckoutPage = lazy(() =>
  import("./pages/CheckOut/CheckOut")
);



// ---------------- LOADER ----------------

const PageLoader = () => {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-10 h-10 border-4 border-black border-t-transparent rounded-full animate-spin" />
    </div>
  );
};

const App = () => {
  // ---------------- MEMOIZED FUNCTION ----------------

  const addToCart = useCallback((product) => {
    console.log("Added to cart:", product);

    // If using Redux/Context later,
    // dispatch action here instead of local state
  }, []);

  // ---------------- ANALYTICS ----------------

  useEffect(() => {
    // Perf: gtag.js (~161 KiB) competes during the LCP window, so GA loads ONLY on
    // the first real user interaction (no idle fallback). Interacting sessions
    // (nearly all real visits) get GA + the landing pageview; pure no-interaction
    // bounces are intentionally not tracked, to keep the initial load clean.
    // Meta PageView is already guaranteed via the image beacon + CAPI.
    let done = false;
    const startGA = () => {
      if (done) return;
      done = true;
      cleanup();
      initGA();
    };
    const events = ["pointerdown", "keydown", "touchstart", "scroll"];
    const cleanup = () =>
      events.forEach((ev) => window.removeEventListener(ev, startGA));
    events.forEach((ev) =>
      window.addEventListener(ev, startGA, { once: true, passive: true })
    );
    return cleanup;
  }, []);

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
        theme="light"
      />

      <Router>
        <TrackPageView />
        <ScrollToTop />

        {/* Memoize Header/Footer separately using React.memo */}
        <Header />

        <div className="pt-[100px]">
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Home />} />

              <Route
                path="/women"
                element={<Women addToCart={addToCart} />}
              />

              <Route
                path="/men"
                element={<Men addToCart={addToCart} />}
              />

              <Route path="/about" element={<About />} />

              <Route
                path="/new-arrivals"
                element={<NewArrivals />}
              />

              <Route
                path="/best-seller"
                element={<BestSeller />}
              />

              <Route
                path="/product-details/:slug"
                element={<ProductDetails />}
              />


              <Route
                path="/checkout"
                element={<CheckoutPage />}
              />

              <Route
                path="/contact-us"
                element={<ContactUs />}
              />

              <Route path="/cart" element={<ShoppingCart />} />

              <Route path="/my-cart" element={<Cart />} />

              <Route
                path="/privacy-policy"
                element={<PrivacyPolicyPage />}
              />

              <Route
                path="/shipping-delivery"
                element={<ShippingDeliveryPage />}
              />

              <Route
                path="/terms-conditions"
                element={<TermsPage />}
              />

              <Route
                path="/return-exchange"
                element={<ReturnExchangePage />}
              />

              <Route
                path="/order-success"
                element={<OrderSuccessPage />}
              />

              <Route
                path="/payment-failed"
                element={<PaymentFailedPage />}
              />

              <Route
                path="/refund-policy"
                element={<RefundPolicyPage />}
              />

              <Route
                path="/payment-pending"
                element={<PaymentPendingPage />}
              />

              <Route
                path="/check_payment_status"
                element={<PaymentStatus />}
              />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </div>

        <Footer />
      </Router>
    </>
  );
};

export default React.memo(App);